<script setup>
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillsCell from '@js/components/tables/cells/PillsCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { role, archived, active } = defineProps({
    role: {
        type: String,
        required: false,
    },
    archived: {
        type: Boolean,
        required: false,
    },
    active: {
        type: Boolean,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'id',
            header: () => 'ID',
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.id,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'firstname',
            header: () => 'Prénom',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.firstname,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'lastname',
            header: () => 'Nom',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.lastname,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'email',
            header: () => 'Email',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.email,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'associatedSubAccounts',
            header: () => 'Sous-comptes associés',
            cell: (props) =>
                h(PillsCell, {
                    pillValues: props.row.original.tenants.map((tenant) => {
                        return {
                            value: tenant.name,
                            type: 'secondary',
                        };
                    }),
                }),
            enableSorting: false,
        }),
        columnHelper.display({
            id: 'actions',
            header: 'Actions',
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Modifier',
                                href: route('front.updateUser', {
                                    id: props.row.original.id,
                                }),
                                icon: PencilSimpleIcon,
                            }),
                    },
                ),
            width: 0,
        }),
    ],
    url: route('front.postUsers'),
    data: {
        role,
        archived,
        active,
    },
    columnVisibility: {
        associatedSubAccounts: role === 'admin' || role === 'multi_account',
    },
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                size="small"
                :left-icon="PlusIcon"
                :href="route('front.createUser')"
            >
                Ajouter un Super Admin, un Admin ou un Multi-compte
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
